import React, { useEffect, useRef, useState } from 'react';
import { Phone, Sparkles } from 'lucide-react';
import Reveal from './shared/Reveal';

const stats = [
  { value: 3, suffix: '+', label: 'Years of Experience' },
  { value: 95, suffix: '%', label: 'Visa Success Rate' },
  { value: 20, suffix: '+', label: 'Expert Counselors' },
  { value: 24, suffix: '/7', label: 'Support Available' }
];

/** Counts up when scrolled into view */
const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const duration = 1400;
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.round(eased * value));
          if (progress < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => { observer.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);

  return (
    <span ref={ref} className="font-display text-4xl lg:text-5xl font-extrabold text-white tabular-nums">
      {display}
      <span className="text-brand-400">{suffix}</span>
    </span>
  );
};

const CTASection = () => {
  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="container-x">
        <Reveal>
          <div className="relative rounded-3xl bg-navy-900 overflow-hidden shadow-lift">
            {/* Decorative glows */}
            <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-brand-500/25 blur-3xl"></div>
            <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-navy-500/40 blur-3xl"></div>
            {/* Dot grid texture */}
            <div
              className="absolute inset-0 opacity-[0.07]"
              style={{
                backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                backgroundSize: '22px 22px'
              }}
            ></div>

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 p-8 sm:p-12 lg:p-16 items-center">
              {/* Left — copy + phone CTAs */}
              <div>
                <span className="inline-flex items-center gap-2 text-brand-300 text-xs font-bold tracking-[0.18em] uppercase">
                  <Sparkles className="w-4 h-4" />
                  Talk to a counselor
                </span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-white leading-tight">
                  Ready to start your journey?
                </h2>
                <p className="mt-4 text-navy-100/80 leading-relaxed max-w-md">
                  Get personalized guidance from our expert counselors and take the
                  first step toward your international education goals — today.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
                  <a href="tel:+917674099636" className="btn-primary">
                    <Phone className="w-4 h-4" />
                    +91 76740 99636
                  </a>
                  <a href="tel:+919059333987" className="btn-outline-light">
                    <Phone className="w-4 h-4" />
                    +91 90593 33987
                  </a>
                </div>
              </div>

              {/* Right — animated stats */}
              <div className="grid grid-cols-2 gap-4 lg:gap-5">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-6 lg:p-7 hover:bg-white/[0.1] transition-colors duration-300"
                  >
                    <Counter value={stat.value} suffix={stat.suffix} />
                    <p className="mt-2 text-sm font-medium text-navy-100/70">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CTASection;
